import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { getNode, clearRunningNode, runNode } from "actions/workflows";
import Input from "components/Elements/Input";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";

const TestNodeModal = NiceModal.create(({ node_id }) => {
  const dispatch = useDispatch();
  const { node, runningNode, nodeResults } = useSelector(
    (state) => state.workflows
  );
  const [values, setValues] = useState({});
  const [properties, setProperties] = useState({});
  const [context, setContext] = useState("{}");
  const modal = useModal();

  useEffect(() => {
    dispatch(getNode(node_id));
    // eslint-disable-next-line
  }, []);

  const hide = (e) => {
    e.stopPropagation();
    dispatch(clearRunningNode());
    modal.resolve();
    modal.remove();
  };

  const handleChange = ({ target }) => {
    const u = { ...values };
    u[target.name] = target.value;
    setValues(u);
  };

  const handlePropertyChange = ({ target }) => {
    const u = { ...properties };
    u[target.name] = target.value;
    setProperties(u);
  };

  const renderNodeInputs = (node) => {
    const inputs = JSON.parse(node.input_params);
    const inputFields = [];
    Object.keys(inputs).forEach((name) => {
      const input = inputs[name];
      inputFields.push(
        <div key={name} className="mb-4">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor={name}
          >
            {name} ({input.type})
          </label>
          {input.type === "int" ||
          input.type === "float" ||
          input.type === "number" ? (
            <Input
              type="number"
              label={name}
              placeholder={input.description}
              name={name}
              value={values[name] || ""}
              onChange={handleChange}
            />
          ) : (
            <Input
              type="textarea"
              label={name}
              placeholder={input.description}
              name={name}
              value={values[name] || ""}
              onChange={handleChange}
              style={{
                height: "100px",
              }}
            />
          )}
        </div>
      );
    });

    return inputFields;
  };

  const renderNodeProperties = (node) => {
    console.log("PROPERTIES", node.properties);
    if (!node.properties) {
      return null;
    }
    const props = JSON.parse(node.properties);
    const propertyFields = [];
    Object.keys(props).forEach((name) => {
      const property = props[name];
      propertyFields.push(
        <div key={`property_${name}`} className="mb-4">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor={name}
          >
            {name} ({property.type})
          </label>
          {property.type === "int" ||
          property.type === "float" ||
          property.type === "number" ? (
            <Input
              type="number"
              label={name}
              placeholder={property.description}
              name={name}
              value={properties[name] || property.default}
              onChange={handlePropertyChange}
            />
          ) : (
            <Input
              type="textarea"
              label={name}
              placeholder={property.description}
              name={name}
              value={properties[name] || property.default}
              onChange={handlePropertyChange}
            />
          )}
        </div>
      );
    });

    return propertyFields;
  };

  return (
    <>
      <div className="w-9/12 h-auto force-height-90 force-center items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative h-full w-full  max-w-3xl overflow-auto">
          {/*content*/}
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            {/*header*/}
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 className="text-3xl font-semibold">
                {node ? `Testing: ${node.name}` : "Loading Node..."}
              </h3>
            </div>
            <div style={{ paddingLeft: "20px" }}>
              {node &&
                node.use_llm &&
                `${node.llm} - ${node.max_tokens} max tokens - ${node.temperature} temperature`}
            </div>
            {/*body*/}
            <div className="relative p-6 flex-auto ">
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="context"
                >
                  Context (JSON)
                </label>
                <CodeMirror
                  extensions={[json()]}
                  label="Context"
                  name="context"
                  value={context}
                  onChange={(code) => setContext(code)}
                />
              </div>
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <label>Input Values</label>
                {node ? renderNodeInputs(node) : "Loading Node Inputs..."}
              </div>
              {node &&
                node.properties !== null &&
                node.properties.trim() !== "" && (
                  <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                    <label>Properties</label>
                    {renderNodeProperties(node)}
                  </div>
                )}
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <Input
                  type="textarea"
                  label="Output Text"
                  placeholder="Output"
                  name="output"
                  value={
                    nodeResults?.results
                      ? JSON.stringify(nodeResults.results)
                      : ""
                  }
                  disabled={true}
                  style={{ height: "100px" }}
                />
              </div>
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <Input
                  type="textarea"
                  label="Output Context"
                  placeholder="Output Context"
                  name="output_context"
                  value={
                    nodeResults?.context
                      ? JSON.stringify(nodeResults.context)
                      : ""
                  }
                  disabled={true}
                  style={{ height: "100px" }}
                />
              </div>
            </div>

            {/*footer*/}
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              {!runningNode ? (
                <button
                  className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={hide}
                >
                  Close
                </button>
              ) : null}
              <button
                className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                onClick={() =>
                  dispatch(runNode(node.public_id, values, properties, context))
                }
                disabled={runningNode}
              >
                {runningNode ? "Running..." : "Run"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
});

export default TestNodeModal;
